import { useEffect, useState, useMemo, useCallback } from "react";
import {
  getAllLeaves,
  updateLeaveStatus,
} from "../../api/leaveApi";
import Loader from "../../components/Loader";
import { formatDateDMY } from "../../utils/dateFormatter";

/* ═══════════════════════════════════════════════════════
   PREMIUM TOAST SYSTEM
═══════════════════════════════════════════════════════ */
const TOAST_DURATION = 3500;

const ToastContainer = ({ toasts, remove }) => (
  <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 9999,
    display: "flex", flexDirection: "column", gap: "10px", pointerEvents: "none" }}>
    {toasts.map((t) => (
      <div key={t.id} style={{
        pointerEvents: "auto", minWidth: "300px", maxWidth: "380px",
        padding: "16px 20px", borderRadius: "16px",
        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
        display: "flex", alignItems: "flex-start", gap: "12px",
        fontFamily: "inherit", fontSize: "14px",
        background: t.type === "success" ? "#f0fdf4" : t.type === "error" ? "#fef2f2" : "#eff6ff",
        border: `1px solid ${t.type === "success" ? "#86efac" : t.type === "error" ? "#fca5a5" : "#93c5fd"}`,
        color: t.type === "success" ? "#166534" : t.type === "error" ? "#991b1b" : "#1e40af",
        animation: "slideIn 0.25s ease",
      }}>
        <span style={{ fontSize: "20px", lineHeight: 1 }}>
          {t.type === "success" ? "✅" : t.type === "error" ? "❌" : "ℹ️"}
        </span>
        <span style={{ flex: 1, lineHeight: "1.5", fontWeight: 500 }}>{t.message}</span>
        <button onClick={() => remove(t.id)} style={{ background: "none", border: "none",
          cursor: "pointer", fontSize: "18px", opacity: 0.5, padding: 0, color: "inherit" }}>×</button>
      </div>
    ))}
    <style>{`
      @keyframes slideIn { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:translateX(0); } }
      @keyframes modalIn { from { opacity:0; transform:scale(0.92) translateY(16px); } to { opacity:1; transform:scale(1) translateY(0); } }
      @keyframes spin    { to { transform:rotate(360deg); } }
    `}</style>
  </div>
);

/* ═══════════════════════════════════════════════════════
   APPROVE CONFIRM MODAL
═══════════════════════════════════════════════════════ */
const ApproveConfirmModal = ({ open, leave, onCancel, onConfirm }) => {
  if (!open || !leave) return null;
  const days = leave.leave_days ?? "—";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 px-8 pt-8 pb-6 text-center border-b border-emerald-100">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
            <span className="text-3xl">✅</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Approve Leave Request</h3>
          <p className="text-sm text-slate-500 mt-1">Are you sure you want to approve this leave request?</p>
        </div>
        <div className="px-8 py-6 space-y-3">
          <DetailRow label="Employee" value={leave.employee_email} />
          <DetailRow label="Leave Type" value={leave.leave_type_name} />
          <DetailRow label="From" value={formatDateDMY(leave.start_date)} />
          <DetailRow label="To" value={formatDateDMY(leave.end_date)} />
          <DetailRow label="Duration" value={`${days} day(s)`} />
        </div>
        <div className="px-8 pb-8 flex gap-3">
          <button onClick={onCancel}
            className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg shadow-emerald-200 hover:opacity-95 transition-all text-sm">
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   APPROVE SUCCESS MODAL
═══════════════════════════════════════════════════════ */
const ApproveSuccessModal = ({ open, onClose }) => {
  useEffect(() => {
    if (open) { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm text-center overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 px-8 pt-8 pb-6 border-b border-emerald-100">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <span className="text-4xl">🎉</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Leave Approved!</h3>
          <p className="text-sm text-emerald-700 mt-2 font-medium">The leave request has been approved successfully.</p>
          <p className="text-xs text-slate-400 mt-1">Notification and email have been sent to the employee.</p>
        </div>
        <div className="px-8 py-6">
          <button onClick={onClose}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg shadow-emerald-200 hover:opacity-95 transition-all text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   REJECT MODAL (confirm + reason input)
═══════════════════════════════════════════════════════ */
const RejectModal = ({ open, leave, remark, setRemark, onCancel, onConfirm, isLoading }) => {
  if (!open || !leave) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}>
        <div className="bg-gradient-to-br from-rose-50 to-red-50 px-8 pt-8 pb-6 text-center border-b border-rose-100">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-500/10 flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Reject Leave Request</h3>
          <p className="text-sm text-slate-500 mt-1">Are you sure you want to reject this leave request?</p>
        </div>
        <div className="px-8 py-5 space-y-3">
          <DetailRow label="Employee" value={leave.employee_email} />
          <DetailRow label="Leave Type" value={leave.leave_type_name} />
          <div className="pt-2">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Reason for Rejection <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows="3"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter rejection reason (required)"
              className="w-full border-2 border-slate-200 focus:border-rose-400 focus:outline-none rounded-xl px-4 py-3 text-sm resize-none transition-colors"
            />
          </div>
        </div>
        <div className="px-8 pb-8 flex gap-3">
          <button onClick={onCancel} disabled={isLoading}
            className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isLoading}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-rose-500 to-red-600 text-white font-bold shadow-lg shadow-rose-200 hover:opacity-95 transition-all text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {isLoading
              ? <><span style={{ width:"14px",height:"14px",border:"2px solid white",borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",animation:"spin 0.6s linear infinite" }} />Rejecting...</>
              : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   REJECT SUCCESS MODAL
═══════════════════════════════════════════════════════ */
const RejectSuccessModal = ({ open, onClose }) => {
  useEffect(() => {
    if (open) { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm text-center overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}>
        <div className="bg-gradient-to-br from-rose-50 to-red-50 px-8 pt-8 pb-6 border-b border-rose-100">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-rose-500/10 flex items-center justify-center">
            <span className="text-4xl">❌</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Leave Rejected</h3>
          <p className="text-sm text-rose-700 mt-2 font-medium">The leave request has been rejected successfully.</p>
          <p className="text-xs text-slate-400 mt-1">The employee has been notified.</p>
        </div>
        <div className="px-8 py-6">
          <button onClick={onClose}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-rose-500 to-red-600 text-white font-bold shadow-lg shadow-rose-200 hover:opacity-95 transition-all text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Detail row helper ─── */
const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-slate-50 last:border-0">
    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</span>
    <span className="text-sm font-semibold text-slate-700 text-right max-w-[60%] break-all">{value || "—"}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const LeaveApproval = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const [employeeFilter, setEmployeeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  /* approve confirm modal */
  const [approveConfirmLeave, setApproveConfirmLeave] = useState(null);
  const [showApproveSuccess, setShowApproveSuccess] = useState(false);

  /* reject modal */
  const [rejectLeave, setRejectLeave] = useState(null);
  const [remark, setRemark] = useState("");
  const [showRejectSuccess, setShowRejectSuccess] = useState(false);

  /* toasts */
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), TOAST_DURATION);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const loadLeaves = useCallback(() => {
    setLoading(true);
    getAllLeaves()
      .then((res) => setLeaves(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  /* ─── approve flow ─── */
  const handleApprove = (leave) => {
    if (actionLoading[leave.id]) return;
    setApproveConfirmLeave(leave);
  };

  const confirmApprove = async () => {
    const id = approveConfirmLeave.id;
    setApproveConfirmLeave(null);
    setActionLoading((prev) => ({ ...prev, [id]: "APPROVED" }));

    // Optimistic update
    setLeaves((prev) => prev.map((l) => (l.id === id ? { ...l, status: "APPROVED" } : l)));
    setShowApproveSuccess(true);

    try {
      await updateLeaveStatus(id, { status: "APPROVED" });
      loadLeaves();
    } catch {
      setLeaves((prev) => prev.map((l) => (l.id === id ? { ...l, status: "PENDING" } : l)));
      setShowApproveSuccess(false);
      addToast("Unable to approve leave. Please try again.", "error");
    } finally {
      setActionLoading((prev) => { const c = { ...prev }; delete c[id]; return c; });
    }
  };

  /* ─── reject flow ─── */
  const openRejectModal = (leave) => {
    if (actionLoading[leave.id]) return;
    setRejectLeave(leave);
    setRemark("");
  };

  const confirmReject = async () => {
    if (!remark.trim()) { addToast("Rejection remark is required.", "error"); return; }
    const id = rejectLeave.id;
    if (actionLoading[id]) return;

    setActionLoading((prev) => ({ ...prev, [id]: "REJECTED" }));
    setRejectLeave(null);

    // Optimistic update
    setLeaves((prev) => prev.map((l) => l.id === id ? { ...l, status: "REJECTED", rejection_reason: remark } : l));
    setShowRejectSuccess(true);

    try {
      await updateLeaveStatus(id, { status: "REJECTED", rejection_reason: remark });
      loadLeaves();
    } catch {
      setLeaves((prev) => prev.map((l) => l.id === id ? { ...l, status: "PENDING", rejection_reason: "" } : l));
      setShowRejectSuccess(false);
      addToast("Unable to reject leave. Please try again.", "error");
    } finally {
      setActionLoading((prev) => { const c = { ...prev }; delete c[id]; return c; });
    }
  };

  /* ─── filtering ─── */
  const employeeOptions = [...new Set(leaves.map((l) => l.employee_email).filter(Boolean))];

  const filteredLeaves = useMemo(() => {
    return leaves.filter((leave) => {
      const empOk = employeeFilter === "ALL" || leave.employee_email === employeeFilter;
      const statusOk = statusFilter === "ALL" || leave.status === statusFilter;
      return empOk && statusOk;
    });
  }, [leaves, employeeFilter, statusFilter]);

  const StatusBadge = ({ status }) => {
    const map = {
      APPROVED: { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-500" },
      REJECTED: { cls: "bg-rose-50 text-rose-700 border border-rose-200", dot: "bg-rose-500" },
      PENDING: { cls: "bg-amber-50 text-amber-700 border border-amber-200", dot: "bg-amber-500" },
    };
    const c = map[status] || { cls: "bg-slate-50 text-slate-600 border border-slate-200", dot: "bg-slate-400" };
    return (
      <span className={`px-3 py-1 text-xs rounded-full font-semibold inline-flex items-center gap-1.5 ${c.cls}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
        {status}
      </span>
    );
  };

  return (
    <>
      <ToastContainer toasts={toasts} remove={removeToast} />

      {/* Approve confirm */}
      <ApproveConfirmModal
        open={!!approveConfirmLeave}
        leave={approveConfirmLeave}
        onCancel={() => setApproveConfirmLeave(null)}
        onConfirm={confirmApprove}
      />
      {/* Approve success */}
      <ApproveSuccessModal open={showApproveSuccess} onClose={() => setShowApproveSuccess(false)} />

      {/* Reject modal */}
      <RejectModal
        open={!!rejectLeave}
        leave={rejectLeave}
        remark={remark}
        setRemark={setRemark}
        onCancel={() => setRejectLeave(null)}
        onConfirm={confirmReject}
        isLoading={!!actionLoading[rejectLeave?.id]}
      />
      {/* Reject success */}
      <RejectSuccessModal open={showRejectSuccess} onClose={() => setShowRejectSuccess(false)} />

      <div className="space-y-8">
        {/* HEADER */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Leave Approvals</h2>
          <p className="text-sm text-gray-500">Review and manage employee leave requests</p>
        </div>

        {/* FILTERS */}
        <div className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Employee</label>
            <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
              <option value="ALL">All Employees</option>
              {employeeOptions.map((email) => (<option key={email}>{email}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>

        {/* LEAVE TABLE */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <Loader />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200/80">
                  <tr>
                    {["Employee","Leave Type","From","To","Reason","Status / Action"].map((h) => (
                      <th key={h} className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider text-center first:text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-10 text-center text-slate-400 font-medium">No leave requests</td>
                    </tr>
                  ) : (
                    filteredLeaves.map((leave) => {
                      const isThisRowLoading = !!actionLoading[leave.id];
                      const thisRowActionType = actionLoading[leave.id];
                      return (
                        <tr key={leave.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-5 font-medium text-slate-700">{leave.employee_email}</td>
                          <td className="py-4 px-5 text-center text-slate-600">{leave.leave_type_name}</td>
                          <td className="py-4 px-5 text-center text-slate-600">{formatDateDMY(leave.start_date)}</td>
                          <td className="py-4 px-5 text-center text-slate-600">{formatDateDMY(leave.end_date)}</td>
                          <td className="py-4 px-5 text-center text-slate-500 max-w-[140px] truncate">{leave.reason || "—"}</td>
                          <td className="py-4 px-5 text-center">
                            {leave.status === "PENDING" ? (
                              <div className="flex justify-center gap-2">
                                {/* APPROVE */}
                                <button
                                  disabled={isThisRowLoading}
                                  onClick={() => handleApprove(leave)}
                                  className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isThisRowLoading && thisRowActionType === "APPROVED" ? (
                                    <><span style={{ width:"11px",height:"11px",border:"2px solid white",borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",animation:"spin 0.6s linear infinite" }} />Approving...</>
                                  ) : "✓ Approve"}
                                </button>
                                {/* REJECT */}
                                <button
                                  disabled={isThisRowLoading}
                                  onClick={() => openRejectModal(leave)}
                                  className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isThisRowLoading && thisRowActionType === "REJECTED" ? (
                                    <><span style={{ width:"11px",height:"11px",border:"2px solid white",borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",animation:"spin 0.6s linear infinite" }} />Rejecting...</>
                                  ) : "✕ Reject"}
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <StatusBadge status={leave.status} />
                                {leave.status === "REJECTED" && leave.rejection_reason && (
                                  <p className="text-xs text-rose-600 mt-1">Reason: {leave.rejection_reason}</p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeaveApproval;
